import { useEffect } from 'react';

/** Whether a key press came from somewhere the user is typing, or from inside an embedded frame. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (
    target.isContentEditable ||
    target.closest('[contenteditable]:not([contenteditable="false"])')
  ) {
    return true;
  }
  return ['INPUT', 'TEXTAREA', 'SELECT', 'IFRAME'].includes(target.tagName);
}

/**
 * Whether F belongs to something other than the viewer: a menu (Radix typeahead reads it) or a second
 * dialog stacked over the viewer (rename, upload, share, delete), whose buttons must not resize the
 * hidden viewer.
 */
function isOtherSurface(target: EventTarget | null): boolean {
  if (target instanceof HTMLElement && target.closest('[role="menu"]')) {
    return true;
  }
  return document.querySelectorAll('[role="dialog"]').length > 1;
}

/** Toggle full screen on a bare F, unless the user is typing, holding a modifier, or the shortcut is off. */
export function useFullScreenShortcut(
  enabled: boolean,
  onToggle: () => void,
): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'f' && event.key !== 'F') return;
      if (event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target) || isOtherSurface(event.target)) return;
      event.preventDefault();
      onToggle();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, onToggle]);
}

/**
 * The dialog's Escape hook: while maximized, the first Escape only leaves full screen. Menu-first comes
 * from Radix's dismissable-layer stack, which hands Escape to the topmost layer only, so this hook never
 * runs while a menu is open. The `defaultPrevented` check is a guard for any Escape already handled.
 */
export function leaveFullScreenOnEscape(
  maximized: boolean,
  exitFullScreen: () => void,
): (event: KeyboardEvent) => void {
  return (event) => {
    if (!maximized || event.defaultPrevented) return;
    event.preventDefault();
    exitFullScreen();
  };
}
